import { IOrganizationEntity } from '@novu/shared';
import { useQuery } from '@tanstack/react-query';
import { FormEvent, useState } from 'react';
import { RiAddLine, RiCheckLine, RiExpandUpDownLine } from 'react-icons/ri';
import { get, post } from '@/api/api.client';
import { Avatar } from '@/components/primitives/avatar';
import { Button } from '@/components/primitives/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/primitives/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/primitives/dropdown-menu';
import { Input } from '@/components/primitives/input';
import { Label } from '@/components/primitives/label';
import { NovuLogoBlackBg } from './icons';
import { useOrganization } from './index';

const JWT_STORAGE_KEY = 'self-hosted-jwt'; // As defined in components.tsx

/**
 * Selector de proyecto (organización) para la edición community (self-hosted). La API ya expone
 * GET/POST /v1/organizations y POST /v1/auth/organizations/:id/switch, pero el dashboard self-hosted
 * solo mostraba el nombre de la organización actual: no se podía crear otra ni cambiar entre ellas.
 */
function OrganizationSwitcherComponent() {
  const { organization, isLoaded } = useOrganization() as {
    organization: { name: string; _id: string } | undefined;
    isLoaded: boolean;
  };
  const [isOpen, setIsOpen] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const { data: organizations } = useQuery({
    queryKey: ['self-hosted-organizations'],
    queryFn: async () => (await get<{ data: IOrganizationEntity[] }>('/organizations')).data,
    enabled: Boolean(organization),
  });

  // El JWT lleva la organización activa: al cambiarla se recarga todo (ambientes, llaves, caché de consultas).
  const switchTo = async (organizationId: string) => {
    const { data: token } = await post<{ data: string }>(`/auth/organizations/${organizationId}/switch`, { body: {} });
    localStorage.setItem(JWT_STORAGE_KEY, token);
    window.location.assign('/');
  };

  const handleSwitch = async (organizationId: string) => {
    if (organizationId === organization?._id) return;

    try {
      await switchTo(organizationId);
    } catch (err) {
      console.error('No se pudo cambiar de proyecto', err);
    }
  };

  const handleCreate = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('Escribe el nombre del proyecto.');

      return;
    }

    setIsSaving(true);
    try {
      const { data: created } = await post<{ data: IOrganizationEntity }>('/organizations', {
        body: { name: name.trim() },
      });
      await switchTo(created._id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo crear el proyecto.');
      setIsSaving(false);
    }
  };

  if (!isLoaded) {
    return (
      <div className="flex w-full items-center gap-2 px-1.5 py-1.5">
        <div className="size-6 animate-pulse rounded-full bg-neutral-alpha-100" />
        <div className="h-4 w-32 animate-pulse rounded bg-neutral-alpha-100" />
      </div>
    );
  }

  if (!organization) return null;

  return (
    <>
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="relative flex w-full items-center justify-start gap-2 rounded-lg px-1.5 py-1.5 hover:bg-neutral-alpha-50 focus:outline-hidden"
          >
            <OrganizationAvatar shining={false} />
            <span className="min-w-0 flex-1 truncate text-left text-sm font-medium text-foreground-950">
              {organization.name}
            </span>
            <RiExpandUpDownLine className="h-3.5 w-3.5 shrink-0 text-gray-500" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          className="max-h-[60vh] w-[260px] overflow-y-auto border border-gray-200 bg-white shadow-sm"
          sideOffset={5}
        >
          {(organizations ?? []).map((org) => (
            <DropdownMenuItem
              key={org._id}
              className="flex cursor-pointer items-center gap-2 text-gray-700 hover:bg-gray-50"
              onClick={() => handleSwitch(org._id)}
            >
              <span className="min-w-0 flex-1 truncate">{org.name}</span>
              {org._id === organization._id && <RiCheckLine className="h-3.5 w-3.5 shrink-0 text-gray-500" />}
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="flex cursor-pointer items-center gap-2 text-gray-700 hover:bg-gray-50"
            onClick={() => {
              setIsOpen(false);
              setName('');
              setError(null);
              setIsCreateOpen(true);
            }}
          >
            <RiAddLine className="h-3.5 w-3.5 shrink-0 text-gray-500" />
            <span>Nuevo proyecto</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="sm:max-w-[420px]">
          <form onSubmit={handleCreate} className="flex flex-col gap-4">
            <DialogHeader>
              <DialogTitle>Nuevo proyecto</DialogTitle>
              <DialogDescription>
                Cada proyecto es una organización aparte, con sus propios ambientes, llaves e integraciones.
              </DialogDescription>
            </DialogHeader>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="new-organization-name">Nombre del proyecto</Label>
              <Input
                id="new-organization-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Mi proyecto"
                required
              />
            </div>

            {error && <p className="text-destructive text-xs">{error}</p>}

            <DialogFooter>
              <Button type="button" variant="secondary" mode="outline" onClick={() => setIsCreateOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" variant="primary" isLoading={isSaving}>
                Crear
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

export { OrganizationSwitcherComponent as OrganizationDropdown, OrganizationSwitcherComponent as OrganizationSwitcher };

const OrganizationAvatar = ({ shining = false }: { shining?: boolean }) => {
  return (
    <Avatar className="relative h-6 w-6 overflow-hidden border-gray-200">
      <NovuLogoBlackBg />
      {shining && (
        <div className="absolute inset-0 before:absolute before:-left-full before:top-0 before:h-full before:w-full before:bg-[linear-gradient(120deg,transparent,rgba(255,255,255,0.3),transparent)] before:transition-all before:duration-[10000ms] before:ease-in-out group-hover:before:left-full"></div>
      )}
    </Avatar>
  );
};
